import React, { useEffect, useRef } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, CheckCircle, Target, Users } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import SEOHead from '../components/SEOHead';
import { solutions, findSolutionBySlug } from '../data/solutions';
import { trackEvent } from '@/lib/tracking';

// Individual solution detail page. Renders one of the three top-level Radius
// solutions with its full service breakdown, ideal-client blurb, outcomes
// list, JSON-LD Service schema, and a Connect CTA.
//
// Fires the GTM `view_service` event exactly once per slug mount, guarded by
// a useRef keyed on the current slug so it cannot refire on re-render but
// will refire when the user navigates between two solution pages.
export const SolutionDetail = () => {
  const { slug } = useParams();
  const solution = findSolutionBySlug(slug);

  const firedFor = useRef(null);
  useEffect(() => {
    if (!slug) return;
    if (firedFor.current === slug) return;
    firedFor.current = slug;
    try {
      trackEvent('view_service', { service_slug: slug });
    } catch {
      /* tracking never breaks the user flow */
    }
  }, [slug]);

  // Unknown slug → send the visitor to the /solutions index rather than
  // showing a bare "not found" screen.
  if (!solution) {
    return <Navigate to="/solutions" replace />;
  }

  const detailUrl = `https://radiusconsulting.co.za/solutions/${solution.slug}`;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: solution.title,
    serviceType: solution.title,
    description: solution.metaDescription,
    url: detailUrl,
    provider: {
      '@type': 'Organization',
      name: 'Radius Consulting',
      url: 'https://radiusconsulting.co.za',
    },
    areaServed: { '@type': 'Country', name: 'South Africa' },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: `${solution.title} — services`,
      itemListElement: solution.services.map((s, i) => ({
        '@type': 'Offer',
        position: i + 1,
        itemOffered: {
          '@type': 'Service',
          name: s.name,
          description: s.description,
        },
      })),
    },
  };

  return (
    <div
      className="solution-detail-page pt-20"
      data-testid={`solution-detail-${solution.slug}`}
    >
      <SEOHead
        title={solution.metaTitle}
        description={solution.metaDescription}
        url={detailUrl}
        schema={schema}
      />

      {/* Hero */}
      <section className="relative overflow-hidden py-24">
        <img
          src={solution.image}
          alt={solution.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-gray-900" />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <Link
            to="/solutions"
            className="inline-flex items-center gap-2 text-gray-300 hover:text-red-500 transition-colors mb-8"
            data-testid="back-to-solutions"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Solutions
          </Link>
          <span className="text-red-500 font-mono text-sm font-semibold mb-4 block">
            {solution.number} · Solution
          </span>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 max-w-4xl leading-tight">
            {solution.title}
          </h1>
          <p className="text-xl sm:text-2xl text-red-400 italic font-light max-w-3xl mb-8">
            &ldquo;{solution.tagline}&rdquo;
          </p>
          <p className="text-base sm:text-lg text-gray-300 leading-relaxed max-w-3xl">
            {solution.description}
          </p>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">What&rsquo;s in scope</h2>
            <p className="text-gray-400 text-base sm:text-lg max-w-2xl leading-relaxed">
              {solution.services.length} services delivered by our in-house team. Take one, take the set.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {solution.services.map((service, idx) => (
              <Card key={idx} className="service-item-card group hover-lift">
                <CardContent className="p-8">
                  <div className="flex items-start gap-3 mb-4">
                    <CheckCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
                    <h3 className="text-xl font-bold text-white group-hover:text-gradient transition-all">
                      {service.name}
                    </h3>
                  </div>
                  <p className="text-gray-400 text-sm leading-relaxed ml-9">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Outcomes + Ideal for */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-red-600/10 rounded-lg flex items-center justify-center">
                <Target className="w-5 h-5 text-red-500" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white">Outcomes</h2>
            </div>
            <ul className="space-y-4">
              {solution.outcomes.map((outcome, idx) => (
                <li key={idx} className="flex items-start gap-3 text-gray-300 leading-relaxed">
                  <ArrowRight className="w-5 h-5 text-red-500 flex-shrink-0 mt-1" />
                  <span>{outcome}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-red-600/10 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-red-500" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white">Ideal for</h2>
            </div>
            <p className="text-gray-300 leading-relaxed text-base sm:text-lg">
              {solution.idealFor}
            </p>
          </div>
        </div>
      </section>

      {/* Other solutions */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-10">Other solutions</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {solutions
              .filter((s) => s.slug !== solution.slug)
              .map((s) => (
                <Link
                  key={s.slug}
                  to={`/solutions/${s.slug}`}
                  className="block group"
                  data-testid={`other-solution-${s.slug}`}
                >
                  <Card className="hover-lift overflow-hidden">
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={s.image}
                        alt={s.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/80" />
                      <div className="absolute inset-0 flex flex-col justify-end p-6">
                        <span className="text-red-500 font-mono text-xs mb-1">{s.number}</span>
                        <h3 className="text-xl font-bold text-white">{s.title}</h3>
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <p className="text-gray-400 text-sm mb-4 line-clamp-2">{s.tagline}</p>
                      <span className="inline-flex items-center gap-2 text-red-500 group-hover:text-red-400 text-sm font-semibold">
                        Explore <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-black">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to build with {solution.title}?
          </h2>
          <p className="text-lg text-gray-400 mb-10 leading-relaxed">
            Tell us where your business is right now and we&rsquo;ll shape the engagement to fit.
          </p>
          <Link to="/contact">
            <Button size="lg" className="btn-primary group" data-testid="solution-cta-connect">
              Connect
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SolutionDetail;
